/**
 * COMPOSANT: TOKEN DASHBOARD V2
 * Dashboard utilisateur avec plan FREE / Pay-as-you-go
 */

import React from 'react';
import { TrendingUp, Activity, Gift, CreditCard, Calendar, Zap } from 'lucide-react';
import { useTokens } from '../../hooks/useTokens';

interface TokenBalance {
 balance: number;
 totalPurchased: number;
 totalUsed: number;
 bonus: number;
 plan: 'free' | 'pay_as_you_go';
 source: 'monthly_free' | 'purchased';
 monthlyFreeTokens: number;
 monthlyTokensUsed: number;
 monthlyResetAt: string | null;
}

export default function TokenDashboard() {
 const tokenData = useTokens();
 
 const balance: TokenBalance = {
 balance: tokenData.balance || 0,
 totalPurchased: tokenData.totalPurchased || 0,
 totalUsed: tokenData.totalUsed || 0,
 bonus: tokenData.breakdown?.bonus || 0, // Tokens bonus (beta testers 500K, parrainage, etc.)
 plan: (tokenData.subscriptionTier === 'free' ? 'free' : 'pay_as_you_go') as 'free' | 'pay_as_you_go',
 source: tokenData.subscriptionTier === 'free' ? 'monthly_free' : 'purchased',
 monthlyFreeTokens: tokenData.monthlyAllowance || 0,
 monthlyTokensUsed: tokenData.monthlyUsed || 0,
 monthlyResetAt: tokenData.resetAt || null,
 };

 const formatTokens = (tokens: number | undefined): string => {
 if (tokens === undefined || tokens === null) return '0';
 return tokens.toLocaleString('fr-FR');
 };

 const getPlanLabel = (plan: string) => {
 const planLabels: Record<string, string> = {
 'free': 'Plan Free',
 'pro': 'Plan Pro',
 'business': 'Plan Business',
 'enterprise': 'Plan Enterprise',
 'pay_as_you_go': 'Pay-as-you-go'
 };
 return planLabels[plan] || planLabels[tokenData.subscriptionTier] || 'Plan Free';
 };

 const getPlanColor = (plan: string) => {
 const colors: Record<string, string> = {
 'free': 'text-purple-700 dark:text-purple-400',
 'pro': 'text-green-400',
 'business': 'text-purple-400',
 'enterprise': 'text-indigo-700 dark:text-indigo-400',
 'pay_as_you_go': 'text-cyan-400'
 };
 return colors[plan] || colors[tokenData.subscriptionTier] || 'text-purple-700 dark:text-purple-400';
 };

 if (tokenData.loading) {
 return (
 <div className="flex items-center justify-center py-12">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 dark:border-purple-400" />
 </div>
 );
 }

 return (
 <div className="w-full max-w-7xl mx-auto px-4 py-8 space-y-6">
 {/* Plan actuel */}
 <div className="p-6 rounded-2xl bg-gradient-to-br bg-theme-surface border border-theme-border">
 <div className="flex items-center justify-between">
 <div>
 <h3 className="text-lg font-semibold text-theme-primary mb-1">Plan actuel</h3>
 <p className={`text-2xl font-bold ${getPlanColor(balance.plan)}`}>
 {getPlanLabel(balance.plan)}
 </p>
 {balance.plan === 'free' && balance.monthlyResetAt && (
 <p className="text-sm text-theme-secondary mt-2">
 <Calendar className="w-4 h-4 inline mr-1" />
 Reset mensuel: {new Date(balance.monthlyResetAt).toLocaleDateString('fr-FR', { 
 day: '2-digit', 
 month: 'long', 
 year: 'numeric' 
 })}
 </p>
 )}
 </div>
 <Zap className="w-12 h-12 text-purple-600 dark:text-purple-400 opacity-20" />
 </div>
 </div>

 {/* Stats Cards */}
 <div className="grid md:grid-cols-4 gap-4">
 {/* Balance actuelle */}
 <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-500/10 to-indigo-600/5 border border-purple-600 dark:border-purple-400/20">
 <div className="flex items-center justify-between mb-2">
 <span className="text-sm text-theme-secondary font-medium">
 {balance.plan === 'free' ? 'Tokens FREE restants' : 'Balance Actuelle'}
 </span>
 <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
 </div>
 <div className="text-3xl font-bold text-theme-primary mb-1">
 {formatTokens(balance.balance)}
 </div>
 <div className="text-xs text-theme-tertiary">
 {balance.plan === 'free' 
 ? `sur ${formatTokens(balance.monthlyFreeTokens)} / mois`
 : 'tokens disponibles'}
 </div>
 </div>

 {/* Consommation mensuelle (FREE) ou Total Acheté (Pay) */}
 {balance.plan === 'free' ? (
 <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-600/10 dark:from-purple-600/20 to-blue-600/5 border border-purple-500/20 dark:border-purple-400/30">
 <div className="flex items-center justify-between mb-2">
 <span className="text-sm text-theme-secondary font-medium">Utilisés ce mois</span>
 <Activity className="w-5 h-5 text-purple-600 dark:text-purple-400" />
 </div>
 <div className="text-3xl font-bold text-theme-primary mb-1">
 {formatTokens(balance.monthlyTokensUsed)}
 </div>
 <div className="text-xs text-theme-tertiary">
 {((balance.monthlyTokensUsed / balance.monthlyFreeTokens) * 100).toFixed(0)}% du quota
 </div>
 </div>
 ) : (
 <div className="p-6 rounded-2xl bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20">
 <div className="flex items-center justify-between mb-2">
 <span className="text-sm text-theme-secondary font-medium">Total Acheté</span>
 <CreditCard className="w-5 h-5 text-green-400" />
 </div>
 <div className="text-3xl font-bold text-theme-primary mb-1">
 {formatTokens(balance.totalPurchased)}
 </div>
 <div className="text-xs text-theme-tertiary">depuis le début</div>
 </div>
 )}

 {/* Total utilisé */}
 <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20">
 <div className="flex items-center justify-between mb-2">
 <span className="text-sm text-theme-secondary font-medium">Total Utilisé</span>
 <Activity className="w-5 h-5 text-purple-400" />
 </div>
 <div className="text-3xl font-bold text-theme-primary mb-1">
 {formatTokens(balance.totalUsed)}
 </div>
 <div className="text-xs text-theme-tertiary">
 {balance.plan === 'pay_as_you_go' && balance.totalPurchased > 0
 ? `${((balance.totalUsed / balance.totalPurchased) * 100).toFixed(0)}% du total`
 : 'lifetime'}
 </div>
 </div>

 {/* Bonus */}
 <div className="p-6 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border border-cyan-500/20">
 <div className="flex items-center justify-between mb-2">
 <span className="text-sm text-theme-secondary font-medium">Tokens Bonus</span>
 <Gift className="w-5 h-5 text-cyan-400" />
 </div>
 <div className="text-3xl font-bold text-theme-primary mb-1">
 {formatTokens(balance.bonus)}
 </div>
 <div className="text-xs text-theme-tertiary">parrainages & promos</div>
 </div>
 </div>

 {/* Call to action si plan FREE */}
 {balance.plan === 'free' && (
 <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-500/10 to-indigo-600/5 border border-purple-600 dark:border-purple-400/20">
 <div className="flex items-center justify-between">
 <div>
 <h3 className="text-lg font-semibold text-theme-primary mb-1">
 Besoin de plus de tokens ?
 </h3>
 <p className="text-sm text-theme-secondary">
 Passez au Pay-as-you-go pour acheter des tokens sans limite
 </p>
 </div>
 <button
 onClick={() => window.location.href = '/billing'}
 className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 dark:from-purple-500 dark:via-indigo-500 dark:to-blue-500 hover:scale-105 text-white font-semibold transition-all duration-300 shadow-lg shadow-purple-500/20"
 >
 Acheter des tokens
 </button>
 </div>
 </div>
 )}
 </div>
 );
}

